/**
 * The user access front door, as pure functions (5 Oct).
 *
 * "Request access for a person": who it is for → New or Change → a checklist
 * → each ticked item's own Ivanti form, prefilled → review → Send. The server
 * files and holds; what lives here is the wizard's bookkeeping (which forms to
 * show, in what order, what is held), how an answered form becomes the Send
 * body and back again, and the words and tones the tracker uses.
 *
 * Import-free (types only) so `tests/access.test.mjs` runs it without React.
 */

/** The wizard's key for the New User Request itself (New only). */
export const NEW_USER_ITEM = "new_user";
/** A New case's IT Setup step on the tracker (local IT raises it). */
export const IT_SETUP_ITEM = "it_setup";
/** The wizard's key for the Deactivate User Request itself (Leave, 6 Oct). */
export const DEACTIVATE_ITEM = "deactivate";

/**
 * The wizard's steps, in order. "What do you need?" asks the type and who it
 * is for on one first screen (5 Oct, product owner): a new hire is always New
 * and an existing person always Change, so a separate type step only asked
 * the same question twice.
 */
export const WIZARD_STEPS = ["who", "items", "forms", "review"] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

/** Who the request is for. */
export type WhoKind = "new_hire" | "existing";
/** The request's type: a new hire, a change, or someone leaving (6 Oct). */
export type AccessTypeName = "new" | "change" | "leave";
export type AccessActionName = "add" | "remove";

/** The types a "who" allows: a new hire is New, an existing person is Change. */
export function allowedTypes(who: WhoKind): AccessTypeName[] {
  return who === "new_hire" ? ["new"] : ["change"];
}

/** The type a "who" starts on. */
export function defaultType(who: WhoKind): AccessTypeName {
  return who === "new_hire" ? "new" : "change";
}

/** The "who" a type means: New is for a new hire, Change and Leave for an existing person. */
export function whoForType(type: AccessTypeName): WhoKind {
  return type === "new" ? "new_hire" : "existing";
}

/** A new hire is enough to go on once they have a name. */
export function newHireReady(hire: { name?: string | null }): boolean {
  return (hire.name ?? "").trim().length >= 2;
}

/** The fields a New user needs before Next (19 Oct, UI review finding 6), in screen order. */
export type NewHireField = "access-name" | "access-start" | "access-manager" | "access-reference";

/**
 * What a New user still lacks (19 Oct, UI review finding 6). Not only the
 * name: without Reporting to nobody follows the case or owns the CRM step;
 * without a Reference user (or "No one similar", when Backoffice chooses)
 * Backoffice has nothing to copy; without a Start date Global IT cannot plan
 * the account and the approver sees no "starts …".
 */
export function newHireMissing(hire: {
  name?: string | null;
  startDate?: string | null;
  reportingTo?: unknown;
  referenceUser?: unknown;
  noReference?: boolean | null;
}): NewHireField[] {
  const missing: NewHireField[] = [];
  if ((hire.name ?? "").trim() === "") {
    missing.push("access-name");
  }
  if ((hire.startDate ?? "").trim() === "") {
    missing.push("access-start");
  }
  if (hire.reportingTo == null) {
    missing.push("access-manager");
  }
  if (hire.referenceUser == null && hire.noReference !== true) {
    missing.push("access-reference");
  }
  return missing;
}

/** The wizard's progress bar (finding 6): the step a screen belongs to, 1-based. */
export function wizardStepNumber(step: WizardStep): number {
  return WIZARD_STEPS.indexOf(step) + 1;
}

/** The ticked items in the checklist's own order (not the order they were ticked). */
export function orderedTicked(
  catalogOrder: readonly string[],
  ticked: readonly string[],
): string[] {
  return catalogOrder.filter((key) => ticked.includes(key));
}

/**
 * Items a New hire request does not offer. None since 13 Oct (Rusty):
 * software and folders are part of the New User process again — Global IT
 * sets them up with the account, after the owner approves a listed one. (7 Oct
 * to 13 Oct they were hidden, and a chat prefill naming them was dropped.)
 */
export const NEW_HIRE_HIDDEN_ITEMS: readonly string[] = [];

/** The checklist items a type of request offers, in catalog order. */
export function itemsForType<T extends { key: string }>(
  type: AccessTypeName,
  items: readonly T[],
): T[] {
  return type === "new"
    ? items.filter((item) => !NEW_HIRE_HIDDEN_ITEMS.includes(item.key))
    : [...items];
}

/** The item form's internal name of the resource field (title, path or URL). */
export const RESOURCE_FIELD = "Resource";

/** A New hire item's own form answers by internal name (14 Oct), the resource left out. */
export type NeedDetails = Record<string, string | undefined>;

/** The answers as Send carries them: the item's own fields, filled, the resource left out. */
export function needDetails(
  fields: readonly string[],
  details: NeedDetails | undefined,
): { field: string; value: string }[] {
  return fields
    .filter((field) => field !== RESOURCE_FIELD)
    .map((field) => ({ field, value: (details?.[field] ?? "").trim() }))
    .filter((detail) => detail.value !== "");
}

/**
 * How "Check and send" words a Change (14 Oct): every item filed now; or the
 * ones that need approval wait for it ("matrix": the matrix always names
 * someone); or only a listed title or folder waits for its owner. A removal
 * never waits.
 */
export type ChangeIntro = "change" | "changeApproval" | "changeListed";

export function changeIntroKey(
  ticked: readonly string[],
  actions: Record<string, AccessActionName | undefined>,
  items: readonly { key: string; approval?: string | null }[],
): ChangeIntro {
  const adds = items.filter((item) => ticked.includes(item.key) && actions[item.key] === "add");
  if (adds.some((item) => item.approval === "matrix")) {
    return "changeApproval";
  }
  return adds.some((item) => item.approval === "listed") ? "changeListed" : "change";
}

/**
 * The forms the requester fills, in order. Change: each ticked item's form.
 * New: the New User Request only — since 5 Oct (product owner) HR no longer
 * fills the business forms (they cannot answer alias, company or roles); each
 * step's owner does, prefilled, once the hire is in ITSM.
 */
export function formSequence(
  type: AccessTypeName,
  catalogOrder: readonly string[],
  ticked: readonly string[],
): string[] {
  if (type === "new") {
    return [NEW_USER_ITEM];
  }
  // Leave (6 Oct): the Deactivate User Request only; each removal is filed by
  // its team once HR has confirmed.
  if (type === "leave") {
    return [DEACTIVATE_ITEM];
  }
  return orderedTicked(catalogOrder, ticked);
}

/**
 * The items that wait: every ticked one for New (for the account) and for
 * Leave (for HR to confirm, then each team removes it).
 */
export function heldItems(
  type: AccessTypeName,
  catalogOrder: readonly string[],
  ticked: readonly string[],
): string[] {
  return type === "change" ? [] : orderedTicked(catalogOrder, ticked);
}

/**
 * Leave (6 Oct): the checklist items to tick from what ITSM shows the leaver
 * has — the person profile's systems that have a checklist item — in the
 * checklist's order, once each.
 */
export function onRecordItems(
  systems: readonly { item_key?: string | null }[],
  catalogOrder: readonly string[],
): string[] {
  const keys = new Set(systems.map((system) => system.item_key ?? "").filter(Boolean));
  return catalogOrder.filter((key) => keys.has(key));
}

/** One New item as Send carries it: what HR said, no form. */
export interface AccessNeedShape {
  item: string;
  note: string | null;
  roles: { id: string; level: string | null }[];
  roles_other: string | null;
  /** Software and folders on a new hire (14 Oct): the item form's other answers. */
  details?: { field: string; value: string }[];
  /** 21 Oct: what was kept, removed and added from the reference user. */
  reference?: { kept: string[]; removed: string[]; added: string[] };
}

/** The New checklist as Send's `needs`, in the checklist's order. */
export function needsFor(
  catalogOrder: readonly string[],
  ticked: readonly string[],
  notes: Record<string, string | undefined>,
  roles: Record<string, RoleSelection | undefined>,
  details: Record<string, NeedDetails | undefined> = {},
  fields: Record<string, readonly string[] | undefined> = {},
): AccessNeedShape[] {
  return orderedTicked(catalogOrder, ticked).map((item) => {
    const note = (notes[item] ?? "").trim();
    const asked = needDetails(fields[item] ?? [], details[item]);
    return {
      item,
      note: note === "" ? null : note,
      ...rolesForPrefill(roles[item]),
      ...(asked.length > 0 ? { details: asked } : {}),
    };
  });
}

/** Who takes a New item on, as the review and tracker say it. */
export type StepOwnerKind = "team" | "manager" | "it_setup";

/** The owner kind of a catalog item (`new_owner`), defaulting to its team. */
export function stepOwnerKind(item: { new_owner?: string | null }): StepOwnerKind {
  return item.new_owner === "manager" || item.new_owner === "it_setup" ? item.new_owner : "team";
}

/** Whether every form in the sequence has been answered, so Review can open. */
export function allAnswered(
  sequence: readonly string[],
  answered: Record<string, unknown>,
): boolean {
  return sequence.length > 0 && sequence.every((key) => answered[key] !== undefined);
}

/** Every ticked Change item needs Add or Remove. */
export function actionsChosen(
  type: AccessTypeName,
  ticked: readonly string[],
  actions: Record<string, AccessActionName | undefined>,
): boolean {
  return type !== "change" || ticked.every((key) => actions[key] !== undefined);
}

// ─── Answered forms ────────────────────────────────────────────────────────

/** One answer, as the request form hands it back and the API takes it. */
export interface AccessAnswer {
  parameter_id: string;
  value?: string | null;
  value_ids?: string[] | null;
  attachment_ids?: string[] | null;
  /** A person's name for a directory picker, so it shows by name. */
  display_value?: string | null;
}

/** An item's answered form, as the request form hands it back. */
export interface CollectedForm {
  offeringId: string;
  subject: string;
  description: string;
  answers: AccessAnswer[];
}

/** One answer as Send carries it: every list present, never null. */
export interface SubmittedAnswer {
  parameter_id: string;
  value: string | null;
  value_ids: string[];
  attachment_ids: string[];
}

/** One item of the Send body. */
export interface AccessItemSubmissionShape {
  item: string;
  action: AccessActionName | null;
  offering_id: string;
  subject: string;
  description: string;
  body_lang: string;
  answers: SubmittedAnswer[];
  /** The role picker's choices behind "Roles needed" (5 Oct): the matrix names their approvers. */
  roles?: SubmittedRole[];
}

/** One picked role as the API takes it. */
export interface SubmittedRole {
  id: string;
  level: string | null;
  pre_approved: boolean;
}

/** An answered form as one item of Send. */
export function toSubmission(
  item: string,
  form: CollectedForm,
  action: AccessActionName | null,
  language: string,
  roles: readonly SubmittedRole[] = [],
): AccessItemSubmissionShape {
  return {
    item,
    action,
    roles: [...roles],
    offering_id: form.offeringId,
    subject: form.subject,
    description: form.description,
    body_lang: language,
    answers: form.answers.map((answer) => ({
      parameter_id: answer.parameter_id,
      value: answer.value ?? null,
      value_ids: answer.value_ids ?? [],
      attachment_ids: [],
    })),
  };
}

/**
 * An answered form back as a prefill, to edit it: the person's own answers
 * win over the server's first guess (same parameter, theirs is kept).
 */
export function editPrefill(
  form: CollectedForm,
  serverValues: readonly AccessAnswer[],
): { offeringId: string; subject: string; description: string; values: AccessAnswer[] } {
  const mine = new Set(form.answers.map((answer) => answer.parameter_id));
  const server = new Map(serverValues.map((value) => [value.parameter_id, value]));
  return {
    offeringId: form.offeringId,
    subject: form.subject,
    description: form.description === form.subject ? "" : form.description,
    values: [
      ...form.answers.map((answer) => {
        const known = server.get(answer.parameter_id);
        const id = (answer.value_ids ?? [])[0] ?? answer.value ?? null;
        const knownId = (known?.value_ids ?? [])[0] ?? known?.value ?? null;
        return {
          parameter_id: answer.parameter_id,
          value: answer.value ?? null,
          value_ids: answer.value_ids ?? [],
          // The same person the server named: keep their name for the picker.
          display_value: id !== null && id === knownId ? (known?.display_value ?? null) : null,
        };
      }),
      ...serverValues.filter((value) => !mine.has(value.parameter_id)),
    ],
  };
}

// ─── Tracker ───────────────────────────────────────────────────────────────

export type ItemStateName = "filed" | "held" | "needs_attention";
export type ApprovalStateName = "approved" | "denied" | "pending" | "none";
export type Tone = "success" | "danger" | "warning" | "info" | "neutral" | "accent";

// ─── One status map (19 Oct, UI review finding 3) ─────────────────────────
//
// WHY: "Waiting for approval" was amber on the case, grey on the item and blue
// on the need; "Ready to grant" green on the case and "Next: Backoffice" amber
// on the item. Every status the access pages show (the case's stage, an
// item's state and approvals stage, one approval, the queues) now has one
// meaning here, and the meaning has one colour:
//
//   waiting  -> warning   someone still has to act
//   action   -> accent    the reader has to act (solid badge)
//   done     -> success   approved, finished
//   rejected -> danger    rejected, not given
//   info     -> neutral   informational, not needed, a plain ITSM status

/** What a status means; its colour follows from it. */
export type StatusMeaning = "waiting" | "action" | "done" | "rejected" | "info";

export const MEANING_TONE: Readonly<Record<StatusMeaning, Tone>> = {
  waiting: "warning",
  action: "accent",
  done: "success",
  rejected: "danger",
  info: "neutral",
};

/** Every access status by where it comes from; anything unlisted is "info". */
export const STATUS_MEANING = {
  /** The case's stage (`AccessCaseStage`): the case page header and the list. */
  stage: {
    waiting_account: "waiting",
    next_steps: "waiting",
    waiting_approval: "waiting",
    waiting_validation: "waiting",
    disable_account: "waiting",
    ready_to_grant: "done",
    done: "done",
    rejected: "rejected",
    in_progress: "info",
    closed: "info",
  },
  /** An item's state (`AccessItemState`): the item rows. */
  item: {
    held: "waiting",
    needs_attention: "waiting",
    ready: "waiting",
    it_setup: "waiting",
    awaiting_validation: "waiting",
    awaiting_approval: "waiting",
    rejected: "rejected",
    not_needed: "info",
    not_requested: "info",
    filed: "info",
  },
  /** One approval (`AccessNeedState`): approvals on the case page and My approvals. */
  need: {
    waiting: "waiting",
    no_approver: "waiting",
    approved: "done",
    pre_approved: "done",
    rejected: "rejected",
  },
  /** An item's approvals together (`AccessApprovalsStage`). */
  needsStage: {
    waiting: "waiting",
    ready: "done",
    rejected: "rejected",
  },
  /** An Ivanti approval record's word (older requests). */
  approval: {
    pending: "waiting",
    approved: "done",
    denied: "rejected",
    none: "info",
  },
  /** Backoffice's "Ready to assign": what to do with an item. */
  next: {
    grant: "done",
    close: "rejected",
    work: "info",
  },
  /** Global IT: is the new user in ITSM yet. */
  inItsm: {
    yes: "done",
    check: "waiting",
    no: "info",
  },
} as const satisfies Record<string, Record<string, StatusMeaning>>;

export type StatusDomain = keyof typeof STATUS_MEANING;

/** What a status means ("info" for one the map does not know, e.g. an Ivanti status). */
export function statusMeaning(
  domain: StatusDomain,
  status: string | null | undefined,
): StatusMeaning {
  const table: Readonly<Record<string, StatusMeaning>> = STATUS_MEANING[domain];
  return (status != null ? table[status] : undefined) ?? "info";
}

/** The badge tone of a status, from the one map. */
export function statusTone(domain: StatusDomain, status: string | null | undefined): Tone {
  return MEANING_TONE[statusMeaning(domain, status)];
}

/** The badge tone for an item's state. */
export function itemStateTone(state: string): Tone {
  return statusTone("item", state);
}

/** The badge tone for an item's approval word. */
export function approvalTone(state: string | null | undefined): Tone {
  return statusTone("approval", state);
}

/** The badge tone of one approval need (5 Oct). */
export function needStateTone(state: string): Tone {
  return statusTone("need", state);
}

/**
 * A new hire's software or folder (13 Oct): Global IT Helpdesk sets it up with
 * the account, after a listed owner approves it; it is never "filed". The
 * state's words key then, else null (the item's usual words).
 */
export function withAccountStateKey(item: {
  state: string;
  with_account?: boolean | null;
}): string | null {
  if (item.with_account !== true) {
    return null;
  }
  switch (item.state) {
    case "it_setup":
      return "access.state.with_account";
    case "awaiting_approval":
      return "access.state.awaiting_setup";
    case "rejected":
      return "access.state.rejected_setup";
    default:
      return null;
  }
}

/**
 * Whether an item row shows its approvals stage as a badge of its own (14
 * Oct). Not while it waits for approval or was rejected: the state badge
 * says that already ("Waiting for approval, then filed"), so it read twice.
 */
export function showsStageBadge(item: { state: string; needs_stage?: string | null }): boolean {
  return (
    item.needs_stage != null && item.state !== "awaiting_approval" && item.state !== "rejected"
  );
}

/**
 * The approvals stage's words key (14 Oct): "Approved, ready to grant" only
 * while there is something to grant; a finished item is just "Approved".
 */
export function stageWordsKey(item: {
  needs_stage?: string | null;
  finished?: boolean | null;
  request?: { is_open?: boolean | null } | null;
}): string {
  const done = item.finished === true || item.request?.is_open === false;
  return item.needs_stage === "ready" && done
    ? "access.approval.approved"
    : `access.approvals.stage.${item.needs_stage ?? "waiting"}`;
}

/** A matrix name as people read it (14 Oct): "Kaarsbo, Mette Rubak" -> "Mette Kaarsbo". */
export function firstLast(name: string): string {
  const text = name.trim().replace(/\s+/g, " ");
  const comma = text.indexOf(",");
  if (comma === -1) {
    return text;
  }
  const last = text.slice(0, comma).trim();
  const first =
    text
      .slice(comma + 1)
      .trim()
      .split(" ")[0] ?? "";
  return first && last ? `${first} ${last}` : text;
}

const ISO_DAY = /\b(\d{4})-(\d{2})-(\d{2})\b/g;

/** A rule text with its ISO days in the app's date format ("until 2026-10-27", 14 Oct). */
export function withLocalDates(text: string, format: (date: Date) => string): string {
  return text.replace(ISO_DAY, (whole, year: string, month: string, day: string) => {
    const date = new Date(Number(year), Number(month) - 1, Number(day), 12);
    return Number.isNaN(date.getTime()) || date.getDate() !== Number(day) ? whole : format(date);
  });
}

/** The badge tone of an item's approvals stage (5 Oct). */
export function needsStageTone(stage: string | null | undefined): Tone {
  return statusTone("needsStage", stage ?? "waiting");
}

/** Where "Open prefilled" goes for a held item. */
export function accessItemPath(caseId: string, n: number): string {
  return `/access/${encodeURIComponent(caseId)}/items/${n}`;
}

/** Where a case opens. */
export function accessCasePath(caseId: string): string {
  return `/access/${encodeURIComponent(caseId)}`;
}

/** The translation key of an item's name: the checklist item, or the NUR. */
export function itemLabelKey(item: string): `access.item.${string}` {
  return `access.item.${item}`;
}

/** Items that need a person, across cases. */
export function attentionCount(
  cases: readonly { items?: readonly { state: string }[] | null }[],
): number {
  return cases.reduce(
    (total, item) =>
      total + (item.items ?? []).filter((entry) => entry.state === "needs_attention").length,
    0,
  );
}

// ─── Access cases list: New and Change together (5 Oct) ──────────────────

/**
 * The list's filter. "New starters" was merged into Access on 5 Oct (product
 * owner): one list, a type badge on each case, and All / New / Change.
 */
export type CaseFilter = "all" | "new" | "change" | "leave";

/** The filter from the URL's `?type=` (`/starters` lands on `?type=new`). */
export function parseCaseFilter(value: string | null | undefined): CaseFilter {
  return value === "new" || value === "change" || value === "leave" ? value : "all";
}

/** One row of the merged list: an access case, or a New starter case the front door did not raise. */
export type MergedCase<A, S> =
  | {
      kind: "access";
      id: string;
      type: "new" | "change" | "leave";
      createdAt: string | null;
      startDate: string | null;
      item: A;
    }
  | {
      kind: "starter";
      id: string;
      type: "new";
      createdAt: string | null;
      startDate: string | null;
      item: S;
    };

/**
 * Access cases and New starter cases as one list, newest first. A New case
 * raised through the front door is both (its case id is the New User
 * Request's): it is listed once, as the access case, which links to the
 * starter page. Each source keeps its own visibility rules; this only merges
 * what each already let the reader see.
 */
export function mergeCaseList<
  A extends {
    case_id: string;
    type: string;
    created_at?: string | null;
    start_date?: string | null;
    leaving_date?: string | null;
  },
  S extends { case_id: string; created_at?: string | null; start_date?: string | null },
>(access: readonly A[], starters: readonly S[], filter: CaseFilter): MergedCase<A, S>[] {
  const accessIds = new Set(access.map((item) => item.case_id));
  const rows: MergedCase<A, S>[] = [
    ...access.map(
      (item): MergedCase<A, S> => ({
        kind: "access",
        id: item.case_id,
        type: item.type === "new" || item.type === "leave" ? item.type : "change",
        createdAt: item.created_at ?? null,
        // A leaver's date sorts "Needs your action" as a starter's does.
        startDate: item.start_date ?? item.leaving_date ?? null,
        item,
      }),
    ),
    ...starters
      .filter((item) => !accessIds.has(item.case_id))
      .map(
        (item): MergedCase<A, S> => ({
          kind: "starter",
          id: item.case_id,
          type: "new",
          createdAt: item.created_at ?? null,
          startDate: item.start_date ?? null,
          item,
        }),
      ),
  ];
  return rows
    .filter((row) => filter === "all" || row.type === filter)
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
}

// ─── Stage, waiting on, sections (5 Oct, product owner's UAT) ─────────────
//
// WHY (5 Oct, product owner's UAT): "the tiles should be smaller and show
// what stage it is in … things that are complete or closed should appear
// only in an archive … it should be clear which ones are waiting on what".
// The server works out each case's stage, who it waits on and whether it is
// archived; these only sort the rows into sections, search them, and pick
// the words and tones.

/** The list's three sections. */
export type CaseSection = "action" | "progress" | "archive";
export const CASE_SECTIONS: readonly CaseSection[] = ["action", "progress", "archive"];

/** The parts of an access case the sections read. */
export interface SectionedAccess {
  archived?: boolean | null;
  action_count?: number | null;
}

/** The parts of a starter case the sections read. */
export interface SectionedStarter {
  is_open: boolean;
  read_only?: boolean | null;
  actions?: readonly unknown[] | null;
}

/** Where a row goes: the reader's action first; done or closed to the archive. */
export function caseSection<A extends SectionedAccess, S extends SectionedStarter>(
  row: MergedCase<A, S>,
): CaseSection {
  if (row.kind === "access") {
    if (row.item.archived === true) {
      return "archive";
    }
    return (row.item.action_count ?? 0) > 0 ? "action" : "progress";
  }
  if (row.item.read_only === true || !row.item.is_open) {
    return "archive";
  }
  return (row.item.actions ?? []).length > 0 ? "action" : "progress";
}

/** Newest first. */
function byNewest(a: { createdAt: string | null }, b: { createdAt: string | null }): number {
  return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
}

/**
 * The rows in their sections. "Needs your action" by start date, soonest
 * first (a case with none after), then newest; the others newest first.
 */
export function sectionRows<A extends SectionedAccess, S extends SectionedStarter>(
  rows: readonly MergedCase<A, S>[],
): Record<CaseSection, MergedCase<A, S>[]> {
  const out: Record<CaseSection, MergedCase<A, S>[]> = { action: [], progress: [], archive: [] };
  for (const row of rows) {
    out[caseSection(row)].push(row);
  }
  out.action.sort((a, b) => {
    if (a.startDate !== b.startDate) {
      if (a.startDate === null) {
        return 1;
      }
      if (b.startDate === null) {
        return -1;
      }
      return a.startDate.localeCompare(b.startDate);
    }
    return byNewest(a, b);
  });
  out.progress.sort(byNewest);
  out.archive.sort(byNewest);
  return out;
}

/** Whether a row's search text holds every word typed (case-insensitive). */
export function matchesSearch(text: string, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return true;
  }
  const haystack = text.toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** The parts of an access case the search reads. */
export interface SearchableAccess {
  person_name: string;
  case_number: string;
  items?:
    | readonly {
        item: string;
        offering: string;
        request?: { ticket_number: string } | null;
        needs?:
          | readonly { approver?: string | null; decided_by?: string | null; what: string }[]
          | null;
        approval?: { records?: readonly { approver?: string | null }[] | null } | null;
      }[]
    | null;
  waiting_on?: readonly { who?: string | null }[] | null;
  approvals_summary?: { approved_by?: readonly string[] | null } | null;
  manager?: { display_name?: string | null } | null;
  requested_by?: { display_name?: string | null } | null;
}

/**
 * What the search box looks in for an access case: the person, the request
 * numbers, the systems (their names as the reader reads them), the approvers
 * and who approved, who it waits on, the manager and the requester.
 */
export function accessSearchText(
  item: SearchableAccess,
  itemName: (key: string) => string,
): string {
  const parts: (string | null | undefined)[] = [
    item.person_name,
    item.case_number,
    item.manager?.display_name,
    item.requested_by?.display_name,
    ...(item.approvals_summary?.approved_by ?? []),
    ...(item.waiting_on ?? []).map((wait) => wait.who),
  ];
  for (const entry of item.items ?? []) {
    parts.push(itemName(entry.item), entry.offering, entry.request?.ticket_number);
    for (const need of entry.needs ?? []) {
      parts.push(need.approver, need.decided_by, need.what);
    }
    for (const record of entry.approval?.records ?? []) {
      parts.push(record.approver);
    }
  }
  return parts.filter((part): part is string => typeof part === "string" && part !== "").join(" ");
}

/** The badge tone of a case's stage. */
export function caseStageTone(stage: string | null | undefined): Tone {
  return statusTone("stage", stage);
}

/** The parts of an item the list card's progress line reads. */
export interface ProgressItem {
  item: string;
  finished?: boolean | null;
  needs_stage?: string | null;
}

/** A case's items split by where they stand, each list in item order, without repeats. */
export interface ItemProgress {
  open: string[];
  done: string[];
  rejected: string[];
}

/**
 * The list card's progress line (7 Oct, product owner's UAT): plain words
 * ("Open: 365FO, CRM · Done: Account") where a row of coloured dots used to
 * need a legend. `name` maps an item key to its short name; two items with the
 * same short name (New's and Leave's "Account") are listed once.
 */
export function itemProgress(
  items: readonly ProgressItem[],
  name: (item: string) => string = (item) => item,
): ItemProgress {
  const out: ItemProgress = { open: [], done: [], rejected: [] };
  for (const entry of items) {
    const bucket =
      entry.finished !== true
        ? out.open
        : entry.needs_stage === "rejected"
          ? out.rejected
          : out.done;
    const label = name(entry.item);
    if (!bucket.includes(label)) {
      bucket.push(label);
    }
  }
  return out;
}

/** The short name key of an item, for the list card ("365FO", "CRM"). */
export function itemShortKey(item: string): `access.short.${string}` {
  return `access.short.${item}`;
}

// ─── Role picker (5 Oct) ───────────────────────────────────────────────────

/** One picked role: a role list option, and for Bluestar its level (View or Design). */
export interface RoleChoice {
  id: string;
  level?: string | null;
  /** Power BI (5 Oct): the person holds the role that pre-approves this model. */
  pre_approved?: boolean | null;
}

/** What the picker holds for one item; the server writes it into "Roles needed". */
export interface RoleSelection {
  choices: RoleChoice[];
  other: string;
}

export const EMPTY_ROLES: RoleSelection = { choices: [], other: "" };

/** Tick or untick a choice; a new one starts on the list's first level (View). */
export function toggleRole(
  selection: RoleSelection,
  id: string,
  levels: readonly string[],
): RoleSelection {
  const has = selection.choices.some((choice) => choice.id === id);
  return {
    ...selection,
    choices: has
      ? selection.choices.filter((choice) => choice.id !== id)
      : [...selection.choices, { id, level: levels[0] ?? null }],
  };
}

/** Set one picked choice's level. */
export function setRoleLevel(selection: RoleSelection, id: string, level: string): RoleSelection {
  return {
    ...selection,
    choices: selection.choices.map((choice) => (choice.id === id ? { ...choice, level } : choice)),
  };
}

/** Tick or untick "already holds the role" on one picked Power BI model (5 Oct). */
export function setPreApproved(
  selection: RoleSelection,
  id: string,
  preApproved: boolean,
): RoleSelection {
  return {
    ...selection,
    choices: selection.choices.map((choice) =>
      choice.id === id ? { ...choice, pre_approved: preApproved } : choice,
    ),
  };
}

/** The picks as Send and "Open and complete" carry them, with their pre-approval. */
export function rolesForSubmission(selection: RoleSelection | undefined): SubmittedRole[] {
  return (selection?.choices ?? []).map((choice) => ({
    id: choice.id,
    level: choice.level ?? null,
    pre_approved: choice.pre_approved === true,
  }));
}

/** The prefill body's `roles` / `roles_other` for one item. */
export function rolesForPrefill(selection: RoleSelection | undefined): {
  roles: { id: string; level: string | null }[];
  roles_other: string | null;
} {
  const other = (selection?.other ?? "").trim();
  return {
    roles: (selection?.choices ?? []).map((choice) => ({
      id: choice.id,
      level: choice.level ?? null,
    })),
    roles_other: other === "" ? null : other,
  };
}

/**
 * The picked roles, readable, for "Check and send": "Purchase", "CFG · View",
 * "<Other>: Exflow approver" (the label translated by the caller). An id the
 * list does not know is skipped.
 */
export function roleSummary(
  list: { options?: readonly { id: string; label: string }[] | null } | undefined,
  selection: RoleSelection | undefined,
  otherLabel: string,
): string[] {
  const labels = new Map((list?.options ?? []).map((option) => [option.id, option.label]));
  const lines = (selection?.choices ?? []).flatMap((choice) => {
    const label = labels.get(choice.id);
    if (label === undefined) {
      return [];
    }
    return [choice.level ? `${label} · ${choice.level}` : label];
  });
  const other = (selection?.other ?? "").trim();
  return other === "" ? lines : [...lines, `${otherLabel}: ${other}`];
}

/** Options matching what the person typed in the picker's search (label, hint or group). */
export function filterRoleOptions<
  T extends { label: string; hint?: string | null; group?: string | null },
>(options: readonly T[], query: string): T[] {
  const wanted = query.trim().toLowerCase();
  if (wanted === "") {
    return [...options];
  }
  return options.filter((option) =>
    [option.label, option.hint ?? "", option.group ?? ""].some((text) =>
      text.toLowerCase().includes(wanted),
    ),
  );
}

// ─── "Check and send" (5 Oct) ──────────────────────────────────────────────

/** The parts of an offering parameter the review reads. */
export interface ReviewParameter {
  parameter_id: string;
  name: { text: string };
  type?: string | null;
  internal_name?: string | null;
  lookup?: string | null;
  options?: readonly { value_id: string; label: { text: string } }[] | null;
  sort_order?: number | null;
}

/** One line of an item's review: the question and the person's answer, readable. */
export interface ReviewLine {
  label: string;
  value: string;
  date?: boolean;
}

/**
 * Every answer on one item's form, readable — what the person checks before
 * Send (5 Oct; all answers since 7 Oct, product owner). Pick-lists show their option labels, not ids; a person picker is
 * left out (who the case is for is shown once, above the items); an empty
 * answer is left out.
 */
/**
 * The hint over an item form whose own questions the wizard asks (13 Oct):
 * the Software item's device, request type and why; the Folder item's
 * resource type, access level and why. Null when the form has none of them
 * (the old forms) or the item has no hint.
 */
export function askedHintKey(itemKey: string, askedParameterIds: readonly string[]): string | null {
  if (askedParameterIds.length === 0) {
    return null;
  }
  if (itemKey === "software") {
    return "access.wizard.forms.askedSoftware";
  }
  if (itemKey === "shared_folders") {
    return "access.wizard.forms.askedFolder";
  }
  return null;
}

export function reviewLines(
  parameters: readonly ReviewParameter[],
  answers: readonly AccessAnswer[],
): ReviewLine[] {
  const byId = new Map(answers.map((answer) => [answer.parameter_id, answer]));
  return [...parameters]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .flatMap((parameter): ReviewLine[] => {
      const answer = byId.get(parameter.parameter_id);
      if (answer === undefined || parameter.lookup === "EMPLOYEE") {
        return [];
      }
      // Every answered question shows (7 Oct, product owner's UAT test: a
      // Comment typed on the form was missing here), not only the key ones.
      const label = parameter.name.text;
      const ids = answer.value_ids ?? [];
      let value: string;
      if (ids.length > 0) {
        const labels = new Map(
          (parameter.options ?? []).map((option) => [option.value_id, option.label.text]),
        );
        value = ids.map((id) => labels.get(id) ?? id).join(", ");
      } else {
        value = (answer.value ?? "").trim();
      }
      if (value === "" || value === "false") {
        return [];
      }
      const date = parameter.type === "DATE" || parameter.type === "DATETIME";
      return [{ label, value, ...(date ? { date: true } : {}) }];
    });
}

// ─── Opening the wizard prefilled (6 Oct) ──────────────────────────────────
//
// The chat's Access card and the Ask page's suggestion chips open
// `/access/new` with what is already known, as query parameters the wizard
// reads once when it opens. Every value is a starting point the person can
// change there; nothing is trusted beyond that.

/** What the wizard can be opened with. */
export interface AccessWizardPrefill {
  type?: AccessTypeName | null;
  /**
   * No type, and none picked for them (7 Oct): the chat could not tell a new
   * hire from a change or a leaver, so the wizard opens on its first question
   * with nothing selected. Without it a missing type means New, as before.
   */
  askType?: boolean;
  /** The new hire's name, or the existing person's display name. */
  name?: string | null;
  /** The existing person's Employee RecId (Change). */
  personId?: string | null;
  /** ISO date (YYYY-MM-DD). */
  startDate?: string | null;
  /** A site code ("USLAG") or the words used; matched against the location list. */
  location?: string | null;
  /** Checklist item keys ("365fo", "crm"). */
  items?: readonly string[] | null;
}

const ACCESS_TYPES: readonly AccessTypeName[] = ["new", "change", "leave"];

/**
 * The types the HelpDesk starts (28 Oct, Rusty), as the catalog says, in
 * wizard order; Change when it says nothing valid. New user and Leaver
 * start in the HR Portal unless the server lists them.
 */
export function helpdeskStartTypes(listed: readonly string[] | null | undefined): AccessTypeName[] {
  const types = ACCESS_TYPES.filter((type) => (listed ?? []).includes(type));
  return types.length > 0 ? types : ["change"];
}

/**
 * The wizard's type: the one chosen if the HelpDesk starts it; otherwise the
 * only one it starts, or New while the question is still open.
 */
export function startType(
  chosen: AccessTypeName | null,
  starts: readonly AccessTypeName[],
): AccessTypeName {
  if (chosen !== null && starts.includes(chosen)) {
    return chosen;
  }
  if (starts.length === 1) {
    return starts[0] ?? "change";
  }
  return starts.includes("new") ? "new" : (starts[0] ?? "change");
}
/** `type=ask`: open on the type question with nothing selected (7 Oct). */
const ASK_TYPE = "ask";
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ITEM_KEY = /^[a-z0-9_]{1,40}$/;

/** `/access/new`, with the prefill as query parameters (empty values left out). */
export function accessWizardPath(prefill: AccessWizardPrefill = {}): string {
  const params = new URLSearchParams();
  if (prefill.type) {
    params.set("type", prefill.type);
  } else if (prefill.askType) {
    params.set("type", ASK_TYPE);
  }
  if (prefill.name?.trim()) {
    params.set("name", prefill.name.trim());
  }
  if (prefill.personId?.trim()) {
    params.set("person", prefill.personId.trim());
  }
  if (prefill.startDate && ISO_DATE.test(prefill.startDate)) {
    params.set("start", prefill.startDate);
  }
  if (prefill.location?.trim()) {
    params.set("location", prefill.location.trim());
  }
  const items = (prefill.items ?? []).filter((key) => ITEM_KEY.test(key));
  if (items.length > 0) {
    params.set("items", items.join(","));
  }
  const query = params.toString();
  return query === "" ? "/access/new" : `/access/new?${query}`;
}

/** The wizard's prefill from its query parameters; anything malformed is dropped. */
export function wizardPrefillFromParams(params: URLSearchParams): AccessWizardPrefill {
  const type = params.get("type");
  const start = params.get("start");
  const clip = (value: string | null) => {
    const trimmed = (value ?? "").trim().slice(0, 200);
    return trimmed === "" ? null : trimmed;
  };
  return {
    type: ACCESS_TYPES.includes(type as AccessTypeName) ? (type as AccessTypeName) : null,
    askType: type === ASK_TYPE,
    name: clip(params.get("name")),
    personId: clip(params.get("person")),
    startDate: start !== null && ISO_DATE.test(start) ? start : null,
    location: clip(params.get("location")),
    // `systems` is read too (7 Oct): the same checklist keys, by the name
    // people reach for when writing a link by hand.
    items: [params.get("items"), params.get("systems")]
      .filter((value): value is string => value !== null)
      .join(",")
      .split(",")
      .map((key) => key.trim())
      .filter((key) => ITEM_KEY.test(key)),
  };
}

function squash(value: string): string {
  return value.toLowerCase().replace(/[^0-9a-z]/g, "");
}

/**
 * The location option a hint means, or null: the label itself, else the
 * one option whose site code is the hint ("USLAG" -> "US - USLAG"), else the
 * one label that contains it ("Lagrange" -> "USLAG - LaGrange"). Ambiguous: null.
 */
export function matchLocation(
  hint: string | null | undefined,
  locations: readonly string[],
): string | null {
  const wanted = squash(hint ?? "");
  if (wanted === "") {
    return null;
  }
  const exact = locations.find((label) => squash(label) === wanted);
  if (exact !== undefined) {
    return exact;
  }
  // One option only: "US" is in "US - USLAG" and "US - USWAU" alike.
  const byCode = locations.filter((label) =>
    label
      .split(/[\s\-–,()]+/)
      .map(squash)
      .includes(wanted),
  );
  if (byCode.length === 1) {
    return byCode[0] ?? null;
  }
  if (byCode.length > 1) {
    return null;
  }
  const containing = locations.filter((label) => squash(label).includes(wanted));
  return containing.length === 1 ? (containing[0] ?? null) : null;
}

/** The checklist items to tick from a prefill: only keys the checklist has, once each. */
export function prefillItems(
  items: readonly string[] | null | undefined,
  known: readonly string[],
): string[] {
  return known.filter((key) => (items ?? []).includes(key));
}

// ─── One open case per person (7 Oct, Rusty's UAT) ─────────────────────────

/** Letters NFD does not take apart, folded the way a person reads them. */
const NAME_FOLD: Record<string, string> = {
  ø: "o",
  æ: "ae",
  œ: "oe",
  ł: "l",
  đ: "d",
  ð: "d",
  þ: "th",
  ı: "i",
  ß: "ss",
};

/**
 * A person's name as the duplicate check compares it: no accents, case or
 * extra spaces ("  Tína   TURNER " is "tina turner"; "Søren" is "soren").
 * The server's `normalise_name` does the same.
 */
export function normaliseName(value: string | null | undefined): string {
  const lower = (value ?? "").toLowerCase();
  const folded = Array.from(lower, (char) => NAME_FOLD[char] ?? char).join("");
  return folded
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .split(/\s+/)
    .filter((part) => part !== "")
    .join(" ");
}

/** The open case a new one would duplicate, as the check and the 409 both carry it. */
export interface AccessDuplicateShape {
  existing_case_id: string;
  request_number: string;
  type: string;
  person_name: string;
  requester_name: string;
  started_at?: string | null;
  start_date?: string | null;
  can_open?: boolean;
  contact?: string | null;
  by_name?: boolean;
}

/** Send's 409 `ACCESS_CASE_DUPLICATE`: the open case, out of the envelope's string params. */
export function duplicateFromParams(params: Record<string, string>): AccessDuplicateShape | null {
  const id = params["existing_case_id"];
  if (id === undefined || id === "") {
    return null;
  }
  const text = (key: string): string | null => {
    const value = params[key];
    return value === undefined || value === "" ? null : value;
  };
  return {
    existing_case_id: id,
    request_number: params["request_number"] ?? "",
    type: params["type"] ?? "",
    person_name: params["person_name"] ?? "",
    requester_name: params["requester_name"] ?? "",
    started_at: text("started_at"),
    start_date: text("start_date"),
    can_open: params["can_open"] === "true",
    contact: text("contact"),
    by_name: params["by_name"] === "true",
  };
}

/** Which sentence says it: the open case's type, and whether the start date is known. */
export type DuplicateMessageKey =
  | "access.duplicate.open.new"
  | "access.duplicate.open.newStarts"
  | "access.duplicate.open.change"
  | "access.duplicate.open.leave"
  | "access.duplicate.hidden";

export function duplicateMessageKey(found: AccessDuplicateShape): DuplicateMessageKey {
  if (found.can_open !== true) {
    return "access.duplicate.hidden";
  }
  if (found.type === "change") {
    return "access.duplicate.open.change";
  }
  if (found.type === "leave") {
    return "access.duplicate.open.leave";
  }
  return found.start_date ? "access.duplicate.open.newStarts" : "access.duplicate.open.new";
}

/**
 * Whether the duplicate stops Next and Send: always, unless it matched by
 * name only and someone allowed to (IT, admins) ticked "a different person".
 */
export function duplicateBlocks(
  found: AccessDuplicateShape | null | undefined,
  override: boolean,
  canOverride: boolean,
  type?: string | null,
): boolean {
  // 28 Oct (Rusty): an open case never stops a Change; it is shown, that's all.
  if (found === null || found === undefined || type === "change") {
    return false;
  }
  return !(override && canOverride && found.by_name === true);
}

/**
 * A finished item's "Done" line (7 Oct): approved by whom, else the request's
 * status and date; an item local IT handles with the account has no request
 * of its own, so it says so instead of "Done: Done, —".
 */
export type FinishedLine =
  | { key: "approved" }
  | { key: "withAccount" }
  | { key: "done" }
  | { key: "status"; status: string }
  | { key: "statusDate"; status: string };

export function finishedLineKind(item: {
  state: string;
  request?: { provider_status?: string | null } | null;
  finished_at?: string | null;
  approvedNames: string;
}): FinishedLine {
  if (item.approvedNames !== "") {
    return { key: "approved" };
  }
  if (item.request === null || item.request === undefined) {
    return item.state === "it_setup" ? { key: "withAccount" } : { key: "done" };
  }
  const status = (item.request.provider_status ?? "").trim();
  if (status === "") {
    return { key: "done" };
  }
  return item.finished_at ? { key: "statusDate", status } : { key: "status", status };
}

/**
 * What a prefilled field on an access form says it came from (14 Oct, User
 * Guide capture): every field read "From the new starter request", even on
 * a Change or a leaver's removal. By the kind of case the prefill is for.
 */
export function prefilledNoteKey(
  caseType: string | null | undefined,
): "access.prefilled.new" | "access.prefilled.change" | "access.prefilled.leave" {
  if (caseType === "change") {
    return "access.prefilled.change";
  }
  if (caseType === "leave") {
    return "access.prefilled.leave";
  }
  return "access.prefilled.new";
}

/**
 * Whether "Open and complete" turns "Roles or access needed" into the
 * Bluestar area picker when the system is Bluestar (8 Oct). Not when the step
 * has its own owner picker above the form, and (14 Oct, User Guide capture)
 * not on a leaver's removal: its text "Remove: all Bluestar access for …"
 * is the answer, and the picker would show it as nothing ticked.
 */
export function bluestarPickerOnForm(
  ownerRoles: unknown,
  caseType: string | null | undefined,
): boolean {
  return ownerRoles == null && caseType !== "leave";
}

/**
 * Whether a row offers "Open and complete" (22 Oct, Rusty, demo case 11150):
 * only to whoever completes the step, as the server says in `action_for_you`
 * (its team, the manager for CRM, or an admin), and only while it is ready.
 * Everyone else who sees the case reads the row; the server refuses their
 * submit too (403).
 */
export function canOpenStep(
  item: { state: string; action_for_you?: boolean | null },
  readOnly: boolean,
): boolean {
  return (
    !readOnly &&
    item.action_for_you === true &&
    (item.state === "ready" || item.state === "needs_attention")
  );
}
